import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, ShieldCheck, Check } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { API } from "@/lib/site";

const initial = {
    name: "", email: "", phone: "", age: "", canton: "",
    focus: "", package: "",
    goal: "", goal_since: "", tried: "", life_change: "",
    nutrition_desc: "", allergies: "", meals: "",
    sleep_hours: "", sleep_quality: 5, health_issues: "", medication: "",
    typical_day: "", activity_hours: "", time_available: "",
    family_situation: "", support: "",
    blockers: "", readiness: 5, expectations: "", why_now: "",
    investment: "", other: "",
};

const STEPS = 5;

const Analysis = () => {
    const { t, lang } = useLanguage();
    const [step, setStep] = useState(1);
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const L = t.analysis;
    const F = L.fields;

    useEffect(() => {
        // Pick up package selection from Programs page (sessionStorage) or
        // legacy event-based selection.
        try {
            const stored = sessionStorage.getItem("ella:selected-package");
            if (stored) {
                setForm((f) => ({ ...f, package: stored }));
                sessionStorage.removeItem("ella:selected-package");
            }
        } catch (e) {
            // ignore
        }
        const handler = (e) => {
            setForm((f) => ({ ...f, package: e.detail }));
        };
        window.addEventListener("ella:select-package", handler);
        return () => window.removeEventListener("ella:select-package", handler);
    }, []);

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
    };

    const next = (e) => {
        e?.preventDefault();
        if (step === 1) {
            if (!form.name || !form.email) {
                toast.error(lang === "de" ? "Bitte Name und E-Mail ausfüllen." : "Por favor completa nombre y correo.");
                return;
            }
        }
        if (step < STEPS) setStep((s) => s + 1);
    };
    const back = () => setStep((s) => Math.max(1, s - 1));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(`${API}/analysis`, { ...form, language: lang });
            setDone(true);
            toast.success(L.step_labels.success);
        } catch (err) {
            console.error(err);
            toast.error(L.step_labels.error);
        } finally {
            setSubmitting(false);
        }
    };

    const progress = (step / STEPS) * 100;

    if (done) {
        return (
            <section id="analysis" data-testid="analysis-section" className="py-24 md:py-32 bg-ella-cream">
                <div className="ella-container max-w-2xl text-center">
                    <div className="w-20 h-20 mx-auto rounded-full bg-ella-ink flex items-center justify-center text-ella-gold">
                        <Check className="w-8 h-8" strokeWidth={1.5} />
                    </div>
                    <h2 className="ella-headline text-4xl md:text-5xl mt-8 text-ella-ink">
                        {L.step_labels.success.split("—")[0]}
                    </h2>
                    <p className="mt-6 text-ella-ink/70 font-light">{L.step_labels.success}</p>
                </div>
            </section>
        );
    }

    return (
        <section id="analysis" data-testid="analysis-section" className="py-24 md:py-32 bg-gradient-to-b from-ella-sand to-ella-cream relative overflow-hidden">
            <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-[radial-gradient(circle_at_center,rgba(193,154,107,0.18),transparent_60%)] pointer-events-none" />

            <div className="ella-container grid lg:grid-cols-12 gap-12 relative">
                <div className="lg:col-span-5">
                    <span className="ella-overline" data-testid="analysis-overline">{L.overline}</span>
                    <h2 className="ella-headline text-4xl md:text-5xl lg:text-6xl mt-4 text-ella-ink" data-testid="analysis-title">
                        {L.title}
                    </h2>
                    <p className="mt-6 text-lg text-ella-ink/70 font-light max-w-md">{L.subtitle}</p>

                    {/* Steps indicator */}
                    <ol className="mt-12 space-y-4" data-testid="analysis-steps">
                        {L.steps.map((s, i) => {
                            const n = i + 1;
                            const state = n < step ? "done" : n === step ? "current" : "upcoming";
                            return (
                                <li key={i} className="flex items-center gap-4">
                                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-display transition-all ${state === "done" ? "bg-ella-ink text-ella-gold" : state === "current" ? "bg-ella-gold text-white" : "bg-white border border-ella-line text-ella-muted"}`}>
                                        {state === "done" ? <Check className="w-4 h-4" strokeWidth={2} /> : n}
                                    </span>
                                    <span className={`text-sm ${state === "current" ? "text-ella-ink font-medium" : "text-ella-muted"}`}>
                                        {s}
                                    </span>
                                </li>
                            );
                        })}
                    </ol>

                    <div className="mt-10 p-5 border border-ella-line rounded-2xl bg-white/70 flex items-start gap-3">
                        <ShieldCheck className="w-5 h-5 mt-0.5 text-ella-gold-dark" strokeWidth={1.5} />
                        <p className="text-xs text-ella-muted leading-relaxed">{L.step_labels.privacy}</p>
                    </div>
                </div>

                {/* Form column */}
                <form onSubmit={step === STEPS ? submit : next} data-testid="analysis-form" className="lg:col-span-7 ella-card !p-8 md:!p-12">
                    {/* Progress bar */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-2">
                            <span className="ella-overline">
                                {L.step_labels.step} {step} {L.step_labels.of} {STEPS}
                            </span>
                            <span className="font-display text-sm text-ella-ink">{L.steps[step - 1]}</span>
                        </div>
                        <div className="h-1 bg-ella-line rounded-full overflow-hidden">
                            <div className="h-full bg-ella-gold transition-all duration-500" style={{ width: `${progress}%` }} />
                        </div>
                    </div>

                    {/* STEP 1 — Personal */}
                    {step === 1 && (
                        <div className="space-y-5" data-testid="analysis-step-1">
                            <div className="grid md:grid-cols-2 gap-5">
                                <Field label={F.name + " *"}><input name="name" required value={form.name} onChange={onChange} className="field-input" data-testid="analysis-name" /></Field>
                                <Field label={F.email + " *"}><input name="email" type="email" required value={form.email} onChange={onChange} className="field-input" data-testid="analysis-email" /></Field>
                                <Field label={F.phone}><input name="phone" value={form.phone} onChange={onChange} className="field-input" data-testid="analysis-phone" /></Field>
                                <Field label={F.age}><input name="age" type="number" min="16" max="99" value={form.age} onChange={onChange} className="field-input" data-testid="analysis-age" /></Field>
                                <Field label={F.canton} className="md:col-span-2"><input name="canton" value={form.canton} onChange={onChange} className="field-input" data-testid="analysis-canton" /></Field>
                            </div>
                            <Field label={F.focus}>
                                <div className="mt-3 grid sm:grid-cols-2 gap-2.5" data-testid="analysis-focus-options">
                                    {Object.entries(F.focus_options).map(([k, v]) => (
                                        <label key={k} className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer text-sm transition-all ${form.focus === k ? "border-ella-gold bg-ella-sand" : "border-ella-line hover:border-ella-gold/60"}`}>
                                            <input type="radio" name="focus" value={k} checked={form.focus === k} onChange={onChange} className="accent-ella-ink" />
                                            <span className="text-ella-ink">{v}</span>
                                        </label>
                                    ))}
                                </div>
                            </Field>
                        </div>
                    )}

                    {/* STEP 2 — Goals */}
                    {step === 2 && (
                        <div className="space-y-5" data-testid="analysis-step-2">
                            <Field label={F.goal + " *"}><textarea name="goal" rows={3} required value={form.goal} onChange={onChange} className="field-textarea" data-testid="analysis-goal" /></Field>
                            <div className="grid md:grid-cols-2 gap-5">
                                <Field label={F.goal_since}><input name="goal_since" value={form.goal_since} onChange={onChange} className="field-input" data-testid="analysis-goal-since" /></Field>
                                <Field label={F.tried}><input name="tried" value={form.tried} onChange={onChange} className="field-input" data-testid="analysis-tried" /></Field>
                            </div>
                            <Field label={F.life_change}><textarea name="life_change" rows={3} value={form.life_change} onChange={onChange} className="field-textarea" data-testid="analysis-life-change" /></Field>
                        </div>
                    )}

                    {/* STEP 3 — Body / Health */}
                    {step === 3 && (
                        <div className="space-y-5" data-testid="analysis-step-3">
                            <Field label={F.nutrition_desc}><textarea name="nutrition_desc" rows={2} value={form.nutrition_desc} onChange={onChange} className="field-textarea" data-testid="analysis-nutrition-desc" /></Field>
                            <div className="grid md:grid-cols-2 gap-5">
                                <Field label={F.allergies}><input name="allergies" value={form.allergies} onChange={onChange} className="field-input" data-testid="analysis-allergies" /></Field>
                                <Field label={F.meals}><input name="meals" type="number" min="1" max="10" value={form.meals} onChange={onChange} className="field-input" data-testid="analysis-meals" /></Field>
                                <Field label={F.sleep_hours}><input name="sleep_hours" type="number" min="1" max="14" value={form.sleep_hours} onChange={onChange} className="field-input" data-testid="analysis-sleep-hours" /></Field>
                                <Field label={`${F.sleep_quality}`}>
                                    <div className="mt-3">
                                        <input name="sleep_quality" type="range" min="1" max="10" value={form.sleep_quality} onChange={onChange} className="w-full accent-ella-gold" data-testid="analysis-sleep-quality" />
                                        <div className="flex justify-between text-[10px] uppercase tracking-[0.2em] text-ella-muted mt-1"><span>{L.scale_weak} 1</span><span className="font-display text-ella-ink text-base">{form.sleep_quality}</span><span>10 {L.scale_strong}</span></div>
                                    </div>
                                </Field>
                            </div>
                            <Field label={F.health_issues}><textarea name="health_issues" rows={2} value={form.health_issues} onChange={onChange} className="field-textarea" data-testid="analysis-health" /></Field>
                            <Field label={F.medication}><input name="medication" value={form.medication} onChange={onChange} className="field-input" data-testid="analysis-medication" /></Field>
                        </div>
                    )}

                    {/* STEP 4 — Daily / Family */}
                    {step === 4 && (
                        <div className="space-y-5" data-testid="analysis-step-4">
                            <Field label={F.typical_day}><textarea name="typical_day" rows={3} value={form.typical_day} onChange={onChange} className="field-textarea" data-testid="analysis-typical-day" /></Field>
                            <div className="grid md:grid-cols-2 gap-5">
                                <Field label={F.activity_hours}><input name="activity_hours" type="number" min="0" max="40" value={form.activity_hours} onChange={onChange} className="field-input" data-testid="analysis-activity-hours" /></Field>
                                <Field label={F.time_available}><input name="time_available" type="number" min="0" max="40" value={form.time_available} onChange={onChange} className="field-input" data-testid="analysis-time-available" /></Field>
                            </div>
                            <Field label={F.family_situation}><textarea name="family_situation" rows={3} value={form.family_situation} onChange={onChange} className="field-textarea" data-testid="analysis-family-situation" /></Field>
                            <Field label={F.support}><input name="support" value={form.support} onChange={onChange} className="field-input" data-testid="analysis-support" /></Field>
                        </div>
                    )}

                    {/* STEP 5 — Readiness */}
                    {step === 5 && (
                        <div className="space-y-5" data-testid="analysis-step-5">
                            <Field label={F.blockers}><textarea name="blockers" rows={2} value={form.blockers} onChange={onChange} className="field-textarea" data-testid="analysis-blockers" /></Field>
                            <Field label={F.readiness}>
                                <div className="mt-3">
                                    <input name="readiness" type="range" min="1" max="10" value={form.readiness} onChange={onChange} className="w-full accent-ella-gold" data-testid="analysis-readiness" />
                                    <div className="flex justify-between text-[10px] uppercase tracking-[0.2em] text-ella-muted mt-1"><span>{L.scale_weak} 1</span><span className="font-display text-ella-ink text-base">{form.readiness}</span><span>10 {L.scale_strong}</span></div>
                                </div>
                            </Field>
                            <Field label={F.expectations}><textarea name="expectations" rows={2} value={form.expectations} onChange={onChange} className="field-textarea" data-testid="analysis-expectations" /></Field>
                            <Field label={F.why_now}><input name="why_now" value={form.why_now} onChange={onChange} className="field-input" data-testid="analysis-why-now" /></Field>
                            <Field label={F.investment}>
                                <div className="mt-3 grid sm:grid-cols-2 gap-2.5" data-testid="analysis-investment-options">
                                    {Object.entries(F.investment_options).map(([k, v]) => (
                                        <label key={k} className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer text-sm transition-all ${form.investment === k ? "border-ella-gold bg-ella-sand" : "border-ella-line hover:border-ella-gold/60"}`}>
                                            <input type="radio" name="investment" value={k} checked={form.investment === k} onChange={onChange} className="accent-ella-ink" />
                                            <span className="text-ella-ink">{v}</span>
                                        </label>
                                    ))}
                                </div>
                            </Field>
                            <Field label={F.other}><textarea name="other" rows={2} value={form.other} onChange={onChange} className="field-textarea" data-testid="analysis-other" /></Field>
                        </div>
                    )}

                    {/* Controls */}
                    <div className="mt-10 flex items-center justify-between gap-3">
                        {step > 1 ? (
                            <button type="button" onClick={back} className="btn-outline" data-testid="analysis-back">
                                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> {L.step_labels.back}
                            </button>
                        ) : <span />}

                        {step < STEPS ? (
                            <button type="submit" className="btn-primary" data-testid="analysis-next">
                                {L.step_labels.next} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                        ) : (
                            <button type="submit" disabled={submitting} className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed" data-testid="analysis-submit">
                                {submitting ? L.step_labels.submitting : L.step_labels.submit}
                                {!submitting && <ArrowRight className="w-4 h-4" strokeWidth={1.5} />}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </section>
    );
};

const Field = ({ label, children, className = "" }) => (
    <div className={className}>
        <label className="ella-overline">{label}</label>
        <div className="mt-2">{children}</div>
    </div>
);

export default Analysis;
